// Coprime LFO bank + asymmetric tension envelope. Phase from ABSOLUTE engine time
// (no integration drift). Pure: tension period reroll via injected Rng.

import type { AxisName, AxisVector } from '@shared/parameterState';
import { AXES } from '@shared/parameterState';
import type { Rng } from '@shared/rng';
import { nextRange } from '@shared/rng';
import { smoothstep } from './smoothing';

export const LFO_PERIODS = [137, 191, 263, 311] as const;

export interface LfoRoute {
  lfo: number;
  axis: AxisName;
  depth: number;
}

export const LFO_ROUTES: readonly LfoRoute[] = [
  { lfo: 0, axis: 'register', depth: 0.18 },
  { lfo: 1, axis: 'brightness', depth: 0.15 },
  { lfo: 2, axis: 'space', depth: 0.12 },
  { lfo: 3, axis: 'harmonicity', depth: 0.1 },
];

function zeroVector(): AxisVector {
  const v = {} as AxisVector;
  for (const ax of AXES) v[ax] = 0;
  return v;
}

/** Sample additive per-axis offsets from absolute engine time. depthScale = 0.5 + roaming. */
export function sampleLfos(engineTimeSec: number, depthScale: number): AxisVector {
  const out = zeroVector();
  for (const route of LFO_ROUTES) {
    const period = LFO_PERIODS[route.lfo];
    const phase = (engineTimeSec / period) % 1;
    const value = Math.sin(2 * Math.PI * phase);
    out[route.axis] += route.depth * depthScale * value;
  }
  return out;
}

export const TENSION_RISE_PORTION = 0.7;

/**
 * Asymmetric tension envelope. Period rerolls in [360,720]s on each wrap.
 * 70% rise (slow), 30% fall (faster). Output in [0,1].
 */
export class TensionEnvelope {
  periodSec: number;
  private lastPhase = 0;

  constructor(rng: Rng) {
    this.periodSec = nextRange(rng, 360, 720);
  }

  /** Sample tension at absolute engine time; rerolls period when the cycle wraps. */
  sample(engineTimeSec: number, rng: Rng): number {
    const phase = (engineTimeSec / this.periodSec) % 1;
    if (phase < this.lastPhase) {
      // wrapped — reroll period for the next cycle
      this.periodSec = nextRange(rng, 360, 720);
    }
    this.lastPhase = phase;
    return tensionAt(phase);
  }
}

/** Pure phase->tension shape (exported for tests). */
export function tensionAt(phase: number): number {
  const p = TENSION_RISE_PORTION;
  if (phase < p) return smoothstep(phase / p);
  return 1 - smoothstep((phase - p) / (1 - p));
}

/** Per-axis tension contribution: density +0.20t, harmonicity +0.10t, space -0.08t. */
export function tensionContribution(tension: number): AxisVector {
  const v = zeroVector();
  v.density = 0.2 * tension;
  v.harmonicity = 0.1 * tension;
  v.space = -0.08 * tension;
  return v;
}
